/**
 * SVG Loader · 76 个游戏元素 SVG 加载器
 *
 * 职责：
 *   1. Vite `?url` 静态资源加载：把 SVG 文件编译成 hash URL
 *   2. Image() 预加载 + 缓存：屏 4 战斗 30Hz 渲染时 O(1) 查表
 *   3. CSS 变量读取：把 :root 上的 Ardot token 一次性取到 Map，
 *      供 canvas-renderer 拿真色值（canvas API 拿不到 CSS 变量）
 *   4. 启动期 var() 注入（FR-023 方案 G, 2026-07-05）：
 *      - <img> 加载的 SVG 是独立 document, 拿不到 HTML :root CSS 变量
 *      - canvas drawImage(Image) 同样无法解析 SVG 内部 var()
 *      - 解法：把 SVG 文本里 var(--xxx) 替换成 :root 真色值, 转 data URL
 *      - 首次 getUrl() 懒生成, 缓存到 Map, O(1) 复用
 *      - currentColor 不动(由调用方传 img.style.color 控色)
 *
 * 设计原则（FR-020 §0.2 + Ardot spec §6.1-6.4）：
 *   - 多色元素（瓦片渐变 / tank 车身）→ SVG 内 style 块用 var(--xxx), 启动期替换
 *   - 单色元素（HP / 准星 / Callout）→ SVG 用 currentColor, img.style.color 控色
 *   - canvas 端 Image.style.color 注入
 *
 * 工作模式：
 *   - 屏 4 启动时 await svgLoader.preload([...keys])
 *   - 屏 4 render() 每帧 svgLoader.draw(ctx, 'tank-team-a', px, py)
 *   - 屏 1/2/3 集成时 svgLoader.getUrl(key) 拿 colored data URL 喂 <img src>
 *
 * @author 像素匠 Paul + 架构通 Yara
 * @date 2026-07-05
 * @see design/v3-multitank/fr/
 * @see _整理输出/02-设计风格文档/17-ardot-design-spec.md
 */

/**
 * 76 个 SVG 元素的统一 key 集合（与 FR-020 §7 总览表一一对应, V6 新增 13 个 annotation）
 * 命名约定：kebab-case，无 .svg 后缀
 */
export type SvgKey =
  // tiles (6)
  | "tile-empty" | "tile-obstacle" | "tile-cover"
  | "tile-spawn-a" | "tile-spawn-b" | "tile-unreachable"
  // tanks (3 + 6 单位 + 5 状态)
  | "tank-team-a" | "tank-team-b" | "tank-dead"
  | "unit-tank-a" | "unit-tank-b" | "unit-dog-a" | "unit-dog-b"
  | "unit-drone-a" | "unit-drone-b"
  | "status-icon-m" | "status-icon-a" | "status-icon-c"
  | "status-icon-d" | "status-icon-idle"
  // phase (5)
  | "phase-opening" | "phase-contact" | "phase-climax" | "phase-endgame" | "phase-progress-bar"
  // effects (5)
  | "fire-blast-a" | "fire-blast-b" | "hit-burst" | "death-flash" | "tombstone-glow"
  // hud (23) · 含屏 4 准星系统预留(FR-021 P1,80px/120px 双尺寸)
  | "hp-bar-high" | "hp-bar-mid" | "hp-bar-low"
  | "spawn-marker-a" | "spawn-marker-b"
  | "callout-a-base" | "callout-b-base" | "callout-midline"
  | "callout-spawn-a" | "callout-spawn-b" | "callout-supply"
  | "status-led-online" | "status-led-warn" | "status-led-offline" | "status-led-active"
  | "hud-corner-tl" | "hud-corner-tr" | "hud-corner-bl" | "hud-corner-br"
  | "coord-grid" | "mini-map-battle-easy"
  | "crosshair-80" | "crosshair-120"
  // operators (2)
  | "operator-tank" | "operator-ai-aggro"
  // FR-023 通用 icon(2026-07-05):UI 按钮/标签图标,不再用 emoji
  | "icon-plus" | "icon-book" | "icon-arrow-right" | "icon-trash" | "icon-terminal" | "icon-chart"
  | "icon-sword" | "icon-shield" | "icon-algorithm" | "icon-tip" | "icon-star" | "icon-think" | "icon-tip-card"
  | "icon-search"
  // V6 军事标注 pt (10): 砺锋台战术地图 NATO 点状标记
  | "pt-01-cp" | "pt-02-op" | "pt-03-sp" | "pt-04-bkr" | "pt-05-ruin"
  | "pt-06-twr" | "pt-07-ammo" | "pt-08-brdg" | "pt-09-ford" | "pt-10-tree"
  // V6 战术箭头 ar (3): 主攻/助攻/撤退
  | "ar-01-main-attack" | "ar-02-support-attack" | "ar-07-withdrawal";

/**
 * 屏 4 战场需要预加载的 P0 SVG 集合（FR-020 §8 第一批）
 * 屏 4 启动时一次性 preload，render() 检查 ready 后再画
 */
export const BATTLE_SVG_KEYS: SvgKey[] = [
  // tiles (6) · FR-022 P0
  "tile-empty", "tile-obstacle", "tile-cover", "tile-spawn-a", "tile-spawn-b", "tile-unreachable",
  // tanks (3) · FR-021
  "tank-team-a", "tank-team-b", "tank-dead",
  // hp 3 档 · FR-021
  "hp-bar-high", "hp-bar-mid", "hp-bar-low",
  // spawn marker 2 · FR-022 P0
  "spawn-marker-a", "spawn-marker-b",
  // effects 4 · FR-022 P0
  "hit-burst", "fire-blast-a", "fire-blast-b", "tombstone-glow",
  // phase 4 + status icon 5 · FR-022 P1
  "phase-opening", "phase-contact", "phase-climax", "phase-endgame",
  "status-icon-m", "status-icon-a", "status-icon-c", "status-icon-d", "status-icon-idle",
  // callout 6 · FR-022 P2
  "callout-a-base", "callout-b-base", "callout-midline",
  "callout-spawn-a", "callout-spawn-b", "callout-supply",
];

/**
 * Canvas 取色：从 :root 读 Ardot token，存为 Map
 * 屏 4 启动 canvas-renderer 构造时调一次
 */
const ARDOT_TOKENS = [
  "--tactical-cyan",
  "--valorant-red",
  "--tactical-amber",
  "--tactical-green",
  "--bg-void",
  "--bg-deep",
  "--bg-elevated",
  "--cover-bg",
  "--hp-bar-frame",
  "--text-primary",
  "--text-secondary",
  "--text-muted",
  // 实色高对比: 地图 token (SVG tile 用)
  "--map-bg",
  "--map-empty",
  "--map-cover",
  "--map-cover-stroke",
  "--map-obstacle",
  "--map-obstacle-stroke",
  "--map-obstacle-border",
  "--map-spawn-a",
  "--map-spawn-b",
  "--map-grid-fine",
] as const;

export type ArdotToken = typeof ARDOT_TOKENS[number];

/**
 * 加载完成的 Image 元素 + 原始 URL + 装饰色（默认 currentColor）
 */
interface LoadedSvg {
  img: HTMLImageElement;
  url: string;
  /** SVG 原始尺寸（来自 viewBox） */
  width: number;
  height: number;
}

/**
 * 统一 draw 接口
 * - ctx：Canvas 2D 上下文
 * - key：SVG 标识
 * - x/y/dw/dh：绘制位置 + 目标尺寸（不传 dw/dh 用原始尺寸）
 * - color：可选，覆盖 SVG 内的 currentColor（不传走 SVG 自身定义）
 * - rotate：可选，旋转角度（弧度）
 */
export interface DrawOptions {
  x: number;
  y: number;
  dw?: number;
  dh?: number;
  color?: string;
  rotate?: number;
}

export class SvgLoader {
  private cache = new Map<SvgKey, LoadedSvg>();
  /** 启动期生成的 colored data URL, O(1) 查表, 不重复生成 */
  private coloredUrlCache = new Map<SvgKey, string>();
  private cssVars = new Map<ArdotToken, string>();
  private loadPromises = new Map<SvgKey, Promise<HTMLImageElement>>();

  constructor() {
    this.readCssVars();
  }

  /**
   * 读取 :root 上的 Ardot token
   * 必须在 DOM 挂载后调用（屏 4 canvas-renderer 构造时已挂）
   * FR-029: 改为 public，主题切换时调用方手动刷新
   */
  readCssVars(): void {
    if (typeof document === "undefined") return;
    this.cssVars.clear();
    const styles = getComputedStyle(document.documentElement);
    for (const name of ARDOT_TOKENS) {
      const value = styles.getPropertyValue(name).trim();
      if (value) {
        this.cssVars.set(name, value);
      }
    }
  }

  /**
   * FR-029: 清空所有缓存（Image 元素 + colored data URL + 进行中的加载）
   * 主题切换后调用，确保后续 getUrl/preload 用新 token 重建
   */
  clearCaches(): void {
    this.cache.clear();
    this.coloredUrlCache.clear();
    this.loadPromises.clear();
  }

  /**
   * FR-029: 一站式主题重建
   *   1. 清空缓存（cache + coloredUrlCache + loadPromises）
   *   2. 重读 :root token（Day 模式覆盖值生效）
   *   3. 重新预加载传入的 keys
   * 返回 Promise，调用方 await 完成后才能安全 draw()
   */
  async rebuild(keys: SvgKey[]): Promise<void> {
    this.clearCaches();
    this.readCssVars();
    await this.preload(keys);
  }

  /**
   * 取得某个 CSS 变量解析后的真色值
   * canvas API 拿不到 CSS var，只能通过这个 Map 查
   */
  token(name: ArdotToken): string {
    return this.cssVars.get(name) ?? "";
  }

  /**
   * 启动期 var() 颜色注入（方案 G, 2026-07-05）
   *
   * 为什么需要:
   *   <img> 加载的 SVG 是独立 document, 拿不到 HTML :root 的 CSS 变量;
   *   canvas drawImage(Image) 同样不解析 SVG 内部 var()。
   *   不注入的话, 多色 SVG (tank/tile/operator/phase/hud) 渲染为全黑。
   *
   * 注入策略:
   *   - var(--xxx) → :root 真色值(从 cssVars Map 查)
   *   - currentColor 不动(由调用方 img.style.color 控色, 保留队伍色等动态能力)
   *   - 注入后转 data:image/svg+xml;base64,... 喂 <img>/Image
   *
   * 性能:
   *   - 首次 getUrl(key) 懒生成, 缓存到 coloredUrlCache
   *   - 后续 O(1) 查表, 零运行时字符串拼接
   *   - 63 个 SVG 总启动期注入 < 5ms
   *
   * 部署:
   *   - Vite 编译时把 SVG 文本打包进 bundle, data URL 内存构造, 无网络请求
   *   - CDN/CloudBase 静态托管零差异
   */
  private injectColors(rawText: string): string {
    // 1. var(--xxx) → 真色值
    let colored = rawText.replace(
      /var\(--([a-z0-9-]+)\)/g,
      (match, name) => {
        const tokenName = `--${name}` as ArdotToken;
        const value = this.cssVars.get(tokenName);
        if (!value) {
          // 启动期 self-check: 缺失的 token 警告但不 throw
          // (允许 SVG 里引用 :root 没声明的变量, 浏览器层会 fallback 到 default)
          console.warn(`[svg-loader] CSS var ${match} not found in :root tokens`);
          return match;
        }
        return value;
      }
    );

    // 2. currentColor 保留不动, 由调用方 img.style.color 控色
    //    (屏 1 icon 默认黑色, 屏 4 tank/hit-burst 走 draw 传 color)

    // 3. btoa + base64 data URL
    //    用 encodeURIComponent 处理 unicode 字符(SVG 里可能有中文字体名)
    const encoded = btoa(unescape(encodeURIComponent(colored)));
    return `data:image/svg+xml;base64,${encoded}`;
  }

  /**
   * 取得 colored data URL
   * - 优先 cache 命中
   * - 否则用 SVG_RAW + injectColors 生成, 缓存后返回
   * - 防御: 无 raw 文本时 fallback 到原始 ?url(理论不会触发)
   */
  private getColoredUrl(key: SvgKey): string {
    const cached = this.coloredUrlCache.get(key);
    if (cached) return cached;

    const raw = SVG_RAW[key];
    if (!raw) {
      // 防御性: SVG_RAW 应该跟 SVG_URLS 1:1, 缺失时 fallback
      console.warn(`[svg-loader] SVG_RAW missing for ${key}, falling back to raw URL`);
      return SVG_URLS[key] ?? "";
    }

    const dataUrl = this.injectColors(raw);
    this.coloredUrlCache.set(key, dataUrl);
    return dataUrl;
  }

  /**
   * 预加载一批 SVG（屏 4 启动时 await）
   * 已加载的跳过；并发加载；返回 Promise.all
   */
  async preload(keys: SvgKey[]): Promise<void> {
    await Promise.all(keys.map((k) => this.load(k)));
  }

  /**
   * 加载单个 SVG
   * URL 已经过启动期 var() 注入(方案 G), 直接给 Image 用
   * 如果对应文件不存在，会用 console.warn 但不 throw（保持单 SVG 缺位不影响整体）
   */
  async load(key: SvgKey): Promise<HTMLImageElement> {
    if (this.cache.has(key)) return this.cache.get(key)!.img;
    if (this.loadPromises.has(key)) return this.loadPromises.get(key)!;

    const promise = this.doLoad(key).catch((e) => {
      console.warn(`[svg-loader] failed to load ${key}.svg:`, e);
      // 占位：返回一个 1x1 透明图，drawImage 不报错
      const placeholder = new Image();
      placeholder.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E";
      this.cache.set(key, { img: placeholder, url: "", width: 0, height: 0 });
      return placeholder;
    });
    this.loadPromises.set(key, promise);
    return promise;
  }

  private async doLoad(key: SvgKey): Promise<HTMLImageElement> {
    // 方案 G (FR-023 重构, 2026-07-05): 从 coloredUrlCache 拿 data URL
    //   - colored data URL = SVG 文本里 var(--xxx) 替换成 :root 真色
    //   - <img> 加载 data URL 是独立 document, 但 var() 已无残留, 直接正确渲染
    //   - canvas drawImage(Image) 同样跳过 var() 解析, 直接拿到有色像素
    const url = this.getColoredUrl(key);
    if (!url) {
      throw new Error(`SVG URL not registered: ${key} (check SvgKey union vs SVG_URLS record)`);
    }
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();

    // 读 viewBox 拿原始尺寸（24x24 / 32x32 / 200x200 等）
    // 这里用 Image.naturalWidth/Height（浏览器在 decode 后会填）
    this.cache.set(key, {
      img,
      url,
      width: img.naturalWidth || 24,
      height: img.naturalHeight || 24,
    });
    return img;
  }

  /**
   * 取得某个 SVG 的 colored data URL (供 DOM 端用 img/src / background-image)
   * 方案 G (FR-023 重构, 2026-07-05):
   *   - O(1) 静态查表, 零运行时字符串拼接
   *   - 启动期 var() 已注入, <img src=...> 直接正确渲染多色 SVG
   *   - 单色 icon (currentColor) 仍按调用方 CSS color 渲染
   */
  getUrl(key: SvgKey): string | undefined {
    return this.getColoredUrl(key) || undefined;
  }

  /**
   * 直接绘制 SVG 到 canvas
   * 优先用外部传入的 color（hex 字符串），通过 img.style.color 注入 currentColor
   */
  draw(ctx: CanvasRenderingContext2D, key: SvgKey, opts: DrawOptions): void {
    const loaded = this.cache.get(key);
    if (!loaded) return; // 未预加载或加载失败 → 静默跳过

    const { img, width, height } = loaded;
    const dw = opts.dw ?? width;
    const dh = opts.dh ?? height;

    // currentColor 控色: 给 img 元素设 style.color, 渲染时 SVG 内部 currentColor 解析为这个值
    // 注意: 仅对单色 currentColor 元素有效; 多色元素的 fixed 颜色已通过 injectColors 注入
    if (opts.color) {
      img.style.color = opts.color;
    }

    ctx.save();
    if (opts.rotate) {
      ctx.translate(opts.x + dw / 2, opts.y + dh / 2);
      ctx.rotate(opts.rotate);
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    } else {
      ctx.drawImage(img, opts.x, opts.y, dw, dh);
    }
    ctx.restore();
  }

  /**
   * 检查预加载是否完成（用于 render() ready 守门）
   */
  has(key: SvgKey): boolean {
    return this.cache.has(key);
  }

  /**
   * 全部已加载的 key 数
   */
  size(): number {
    return this.cache.size;
  }
}

//
// 63 个 SVG 的静态 import 映射(generated by tools/build/gen-svg-urls.mjs)
// 重新生成:node tools/build/gen-svg-urls.mjs
// 校验 1:1:node tools/build/gen-svg-urls.mjs --check
//
// 类型系统强制 SvgKey 联合类型 ↔ 此 Record 1:1:
//   漏 key → tsc 报错 "Property X is missing in type"
//   多 key → tsc 报错 "Object literal may only specify known properties"
//   零运行时字符串拼接,不可能再出现"key 形式不匹配"
import deathFlashUrl from "../assets/game-elements/effects/death-flash.svg?url";
import fireBlastAUrl from "../assets/game-elements/effects/fire-blast-a.svg?url";
import fireBlastBUrl from "../assets/game-elements/effects/fire-blast-b.svg?url";
import hitBurstUrl from "../assets/game-elements/effects/hit-burst.svg?url";
import tombstoneGlowUrl from "../assets/game-elements/effects/tombstone-glow.svg?url";
import calloutABaseUrl from "../assets/game-elements/hud/callout-a-base.svg?url";
import calloutBBaseUrl from "../assets/game-elements/hud/callout-b-base.svg?url";
import calloutMidlineUrl from "../assets/game-elements/hud/callout-midline.svg?url";
import calloutSpawnAUrl from "../assets/game-elements/hud/callout-spawn-a.svg?url";
import calloutSpawnBUrl from "../assets/game-elements/hud/callout-spawn-b.svg?url";
import calloutSupplyUrl from "../assets/game-elements/hud/callout-supply.svg?url";
import coordGridUrl from "../assets/game-elements/hud/coord-grid.svg?url";
import crosshair120Url from "../assets/game-elements/hud/crosshair-120.svg?url";
import crosshair80Url from "../assets/game-elements/hud/crosshair-80.svg?url";
import hpBarHighUrl from "../assets/game-elements/hud/hp-bar-high.svg?url";
import hpBarLowUrl from "../assets/game-elements/hud/hp-bar-low.svg?url";
import hpBarMidUrl from "../assets/game-elements/hud/hp-bar-mid.svg?url";
import hudCornerBlUrl from "../assets/game-elements/hud/hud-corner-bl.svg?url";
import hudCornerBrUrl from "../assets/game-elements/hud/hud-corner-br.svg?url";
import hudCornerTlUrl from "../assets/game-elements/hud/hud-corner-tl.svg?url";
import hudCornerTrUrl from "../assets/game-elements/hud/hud-corner-tr.svg?url";
import miniMapBattleEasyUrl from "../assets/game-elements/hud/mini-map-battle-easy.svg?url";
import spawnMarkerAUrl from "../assets/game-elements/hud/spawn-marker-a.svg?url";
import spawnMarkerBUrl from "../assets/game-elements/hud/spawn-marker-b.svg?url";
import statusLedActiveUrl from "../assets/game-elements/hud/status-led-active.svg?url";
import statusLedOfflineUrl from "../assets/game-elements/hud/status-led-offline.svg?url";
import statusLedOnlineUrl from "../assets/game-elements/hud/status-led-online.svg?url";
import statusLedWarnUrl from "../assets/game-elements/hud/status-led-warn.svg?url";
import iconAlgorithmUrl from "../assets/game-elements/icons/icon-algorithm.svg?url";
import iconArrowRightUrl from "../assets/game-elements/icons/icon-arrow-right.svg?url";
import iconBookUrl from "../assets/game-elements/icons/icon-book.svg?url";
import iconChartUrl from "../assets/game-elements/icons/icon-chart.svg?url";
import iconPlusUrl from "../assets/game-elements/icons/icon-plus.svg?url";
import iconSearchUrl from "../assets/game-elements/icons/icon-search.svg?url";
import iconShieldUrl from "../assets/game-elements/icons/icon-shield.svg?url";
import iconStarUrl from "../assets/game-elements/icons/icon-star.svg?url";
import iconSwordUrl from "../assets/game-elements/icons/icon-sword.svg?url";
import iconTerminalUrl from "../assets/game-elements/icons/icon-terminal.svg?url";
import iconThinkUrl from "../assets/game-elements/icons/icon-think.svg?url";
import iconTipCardUrl from "../assets/game-elements/icons/icon-tip-card.svg?url";
import iconTipUrl from "../assets/game-elements/icons/icon-tip.svg?url";
import iconTrashUrl from "../assets/game-elements/icons/icon-trash.svg?url";
import operatorAiAggroUrl from "../assets/game-elements/operators/operator-ai-aggro.svg?url";
import operatorTankUrl from "../assets/game-elements/operators/operator-tank.svg?url";
import phaseClimaxUrl from "../assets/game-elements/phase/phase-climax.svg?url";
import phaseContactUrl from "../assets/game-elements/phase/phase-contact.svg?url";
import phaseEndgameUrl from "../assets/game-elements/phase/phase-endgame.svg?url";
import phaseOpeningUrl from "../assets/game-elements/phase/phase-opening.svg?url";
import phaseProgressBarUrl from "../assets/game-elements/phase/phase-progress-bar.svg?url";
import statusIconAUrl from "../assets/game-elements/tanks/status-icon-a.svg?url";
import statusIconCUrl from "../assets/game-elements/tanks/status-icon-c.svg?url";
import statusIconDUrl from "../assets/game-elements/tanks/status-icon-d.svg?url";
import statusIconIdleUrl from "../assets/game-elements/tanks/status-icon-idle.svg?url";
import statusIconMUrl from "../assets/game-elements/tanks/status-icon-m.svg?url";
import tankDeadUrl from "../assets/game-elements/tanks/tank-dead.svg?url";
import tankTeamAUrl from "../assets/game-elements/tanks/tank-team-a.svg?url";
import tankTeamBUrl from "../assets/game-elements/tanks/tank-team-b.svg?url";
import unitDogAUrl from "../assets/game-elements/tanks/unit-dog-a.svg?url";
import unitDogBUrl from "../assets/game-elements/tanks/unit-dog-b.svg?url";
import unitDroneAUrl from "../assets/game-elements/tanks/unit-drone-a.svg?url";
import unitDroneBUrl from "../assets/game-elements/tanks/unit-drone-b.svg?url";
import unitTankAUrl from "../assets/game-elements/tanks/unit-tank-a.svg?url";
import unitTankBUrl from "../assets/game-elements/tanks/unit-tank-b.svg?url";
import tileCoverUrl from "../assets/game-elements/tiles/tile-cover.svg?url";
import tileEmptyUrl from "../assets/game-elements/tiles/tile-empty.svg?url";
import tileObstacleUrl from "../assets/game-elements/tiles/tile-obstacle.svg?url";
import tileSpawnAUrl from "../assets/game-elements/tiles/tile-spawn-a.svg?url";
import tileSpawnBUrl from "../assets/game-elements/tiles/tile-spawn-b.svg?url";
import tileUnreachableUrl from "../assets/game-elements/tiles/tile-unreachable.svg?url";
// V6 军事标注 pt (10) + 战术箭头 ar (3)
import pt01CpUrl from "../assets/game-elements/annotations/pt-01-cp.svg?url";
import pt02OpUrl from "../assets/game-elements/annotations/pt-02-op.svg?url";
import pt03SpUrl from "../assets/game-elements/annotations/pt-03-sp.svg?url";
import pt04BkrUrl from "../assets/game-elements/annotations/pt-04-bkr.svg?url";
import pt05RuinUrl from "../assets/game-elements/annotations/pt-05-ruin.svg?url";
import pt06TwrUrl from "../assets/game-elements/annotations/pt-06-twr.svg?url";
import pt07AmmoUrl from "../assets/game-elements/annotations/pt-07-ammo.svg?url";
import pt08BrdgUrl from "../assets/game-elements/annotations/pt-08-brdg.svg?url";
import pt09FordUrl from "../assets/game-elements/annotations/pt-09-ford.svg?url";
import pt10TreeUrl from "../assets/game-elements/annotations/pt-10-tree.svg?url";
import ar01MainAttackUrl from "../assets/game-elements/annotations/ar-01-main-attack.svg?url";
import ar02SupportAttackUrl from "../assets/game-elements/annotations/ar-02-support-attack.svg?url";
import ar07WithdrawalUrl from "../assets/game-elements/annotations/ar-07-withdrawal.svg?url";

const SVG_URLS = {
  "death-flash": deathFlashUrl,
  "fire-blast-a": fireBlastAUrl,
  "fire-blast-b": fireBlastBUrl,
  "hit-burst": hitBurstUrl,
  "tombstone-glow": tombstoneGlowUrl,
  "callout-a-base": calloutABaseUrl,
  "callout-b-base": calloutBBaseUrl,
  "callout-midline": calloutMidlineUrl,
  "callout-spawn-a": calloutSpawnAUrl,
  "callout-spawn-b": calloutSpawnBUrl,
  "callout-supply": calloutSupplyUrl,
  "coord-grid": coordGridUrl,
  "crosshair-120": crosshair120Url,
  "crosshair-80": crosshair80Url,
  "hp-bar-high": hpBarHighUrl,
  "hp-bar-low": hpBarLowUrl,
  "hp-bar-mid": hpBarMidUrl,
  "hud-corner-bl": hudCornerBlUrl,
  "hud-corner-br": hudCornerBrUrl,
  "hud-corner-tl": hudCornerTlUrl,
  "hud-corner-tr": hudCornerTrUrl,
  "mini-map-battle-easy": miniMapBattleEasyUrl,
  "spawn-marker-a": spawnMarkerAUrl,
  "spawn-marker-b": spawnMarkerBUrl,
  "status-led-active": statusLedActiveUrl,
  "status-led-offline": statusLedOfflineUrl,
  "status-led-online": statusLedOnlineUrl,
  "status-led-warn": statusLedWarnUrl,
  "icon-algorithm": iconAlgorithmUrl,
  "icon-arrow-right": iconArrowRightUrl,
  "icon-book": iconBookUrl,
  "icon-chart": iconChartUrl,
  "icon-plus": iconPlusUrl,
  "icon-search": iconSearchUrl,
  "icon-shield": iconShieldUrl,
  "icon-star": iconStarUrl,
  "icon-sword": iconSwordUrl,
  "icon-terminal": iconTerminalUrl,
  "icon-think": iconThinkUrl,
  "icon-tip-card": iconTipCardUrl,
  "icon-tip": iconTipUrl,
  "icon-trash": iconTrashUrl,
  "operator-ai-aggro": operatorAiAggroUrl,
  "operator-tank": operatorTankUrl,
  "phase-climax": phaseClimaxUrl,
  "phase-contact": phaseContactUrl,
  "phase-endgame": phaseEndgameUrl,
  "phase-opening": phaseOpeningUrl,
  "phase-progress-bar": phaseProgressBarUrl,
  "status-icon-a": statusIconAUrl,
  "status-icon-c": statusIconCUrl,
  "status-icon-d": statusIconDUrl,
  "status-icon-idle": statusIconIdleUrl,
  "status-icon-m": statusIconMUrl,
  "tank-dead": tankDeadUrl,
  "tank-team-a": tankTeamAUrl,
  "tank-team-b": tankTeamBUrl,
  "unit-dog-a": unitDogAUrl,
  "unit-dog-b": unitDogBUrl,
  "unit-drone-a": unitDroneAUrl,
  "unit-drone-b": unitDroneBUrl,
  "unit-tank-a": unitTankAUrl,
  "unit-tank-b": unitTankBUrl,
  "tile-cover": tileCoverUrl,
  "tile-empty": tileEmptyUrl,
  "tile-obstacle": tileObstacleUrl,
  "tile-spawn-a": tileSpawnAUrl,
  "tile-spawn-b": tileSpawnBUrl,
  "tile-unreachable": tileUnreachableUrl,
  // V6 军事标注 pt + 战术箭头 ar
  "pt-01-cp": pt01CpUrl,
  "pt-02-op": pt02OpUrl,
  "pt-03-sp": pt03SpUrl,
  "pt-04-bkr": pt04BkrUrl,
  "pt-05-ruin": pt05RuinUrl,
  "pt-06-twr": pt06TwrUrl,
  "pt-07-ammo": pt07AmmoUrl,
  "pt-08-brdg": pt08BrdgUrl,
  "pt-09-ford": pt09FordUrl,
  "pt-10-tree": pt10TreeUrl,
  "ar-01-main-attack": ar01MainAttackUrl,
  "ar-02-support-attack": ar02SupportAttackUrl,
  "ar-07-withdrawal": ar07WithdrawalUrl,
} as const satisfies Record<SvgKey, string>;

//
// 76 个 SVG 的 ?raw 静态 import 块(generated by tools/build/gen-svg-raw.mjs)
// 配合 SVG_URLS 用,启动期把 var(--xxx) 替换成 :root 真色,生成 colored data URL
// 重新生成:node tools/build/gen-svg-raw.mjs
// 校验 1:1:node tools/build/gen-svg-raw.mjs --check
//
import deathFlashRaw from "../assets/game-elements/effects/death-flash.svg?raw";
import fireBlastARaw from "../assets/game-elements/effects/fire-blast-a.svg?raw";
import fireBlastBRaw from "../assets/game-elements/effects/fire-blast-b.svg?raw";
import hitBurstRaw from "../assets/game-elements/effects/hit-burst.svg?raw";
import tombstoneGlowRaw from "../assets/game-elements/effects/tombstone-glow.svg?raw";
import calloutABaseRaw from "../assets/game-elements/hud/callout-a-base.svg?raw";
import calloutBBaseRaw from "../assets/game-elements/hud/callout-b-base.svg?raw";
import calloutMidlineRaw from "../assets/game-elements/hud/callout-midline.svg?raw";
import calloutSpawnARaw from "../assets/game-elements/hud/callout-spawn-a.svg?raw";
import calloutSpawnBRaw from "../assets/game-elements/hud/callout-spawn-b.svg?raw";
import calloutSupplyRaw from "../assets/game-elements/hud/callout-supply.svg?raw";
import coordGridRaw from "../assets/game-elements/hud/coord-grid.svg?raw";
import crosshair120Raw from "../assets/game-elements/hud/crosshair-120.svg?raw";
import crosshair80Raw from "../assets/game-elements/hud/crosshair-80.svg?raw";
import hpBarHighRaw from "../assets/game-elements/hud/hp-bar-high.svg?raw";
import hpBarLowRaw from "../assets/game-elements/hud/hp-bar-low.svg?raw";
import hpBarMidRaw from "../assets/game-elements/hud/hp-bar-mid.svg?raw";
import hudCornerBlRaw from "../assets/game-elements/hud/hud-corner-bl.svg?raw";
import hudCornerBrRaw from "../assets/game-elements/hud/hud-corner-br.svg?raw";
import hudCornerTlRaw from "../assets/game-elements/hud/hud-corner-tl.svg?raw";
import hudCornerTrRaw from "../assets/game-elements/hud/hud-corner-tr.svg?raw";
import miniMapBattleEasyRaw from "../assets/game-elements/hud/mini-map-battle-easy.svg?raw";
import spawnMarkerARaw from "../assets/game-elements/hud/spawn-marker-a.svg?raw";
import spawnMarkerBRaw from "../assets/game-elements/hud/spawn-marker-b.svg?raw";
import statusLedActiveRaw from "../assets/game-elements/hud/status-led-active.svg?raw";
import statusLedOfflineRaw from "../assets/game-elements/hud/status-led-offline.svg?raw";
import statusLedOnlineRaw from "../assets/game-elements/hud/status-led-online.svg?raw";
import statusLedWarnRaw from "../assets/game-elements/hud/status-led-warn.svg?raw";
import iconAlgorithmRaw from "../assets/game-elements/icons/icon-algorithm.svg?raw";
import iconArrowRightRaw from "../assets/game-elements/icons/icon-arrow-right.svg?raw";
import iconBookRaw from "../assets/game-elements/icons/icon-book.svg?raw";
import iconChartRaw from "../assets/game-elements/icons/icon-chart.svg?raw";
import iconPlusRaw from "../assets/game-elements/icons/icon-plus.svg?raw";
import iconSearchRaw from "../assets/game-elements/icons/icon-search.svg?raw";
import iconShieldRaw from "../assets/game-elements/icons/icon-shield.svg?raw";
import iconStarRaw from "../assets/game-elements/icons/icon-star.svg?raw";
import iconSwordRaw from "../assets/game-elements/icons/icon-sword.svg?raw";
import iconTerminalRaw from "../assets/game-elements/icons/icon-terminal.svg?raw";
import iconThinkRaw from "../assets/game-elements/icons/icon-think.svg?raw";
import iconTipCardRaw from "../assets/game-elements/icons/icon-tip-card.svg?raw";
import iconTipRaw from "../assets/game-elements/icons/icon-tip.svg?raw";
import iconTrashRaw from "../assets/game-elements/icons/icon-trash.svg?raw";
import operatorAiAggroRaw from "../assets/game-elements/operators/operator-ai-aggro.svg?raw";
import operatorTankRaw from "../assets/game-elements/operators/operator-tank.svg?raw";
import phaseClimaxRaw from "../assets/game-elements/phase/phase-climax.svg?raw";
import phaseContactRaw from "../assets/game-elements/phase/phase-contact.svg?raw";
import phaseEndgameRaw from "../assets/game-elements/phase/phase-endgame.svg?raw";
import phaseOpeningRaw from "../assets/game-elements/phase/phase-opening.svg?raw";
import phaseProgressBarRaw from "../assets/game-elements/phase/phase-progress-bar.svg?raw";
import statusIconARaw from "../assets/game-elements/tanks/status-icon-a.svg?raw";
import statusIconCRaw from "../assets/game-elements/tanks/status-icon-c.svg?raw";
import statusIconDRaw from "../assets/game-elements/tanks/status-icon-d.svg?raw";
import statusIconIdleRaw from "../assets/game-elements/tanks/status-icon-idle.svg?raw";
import statusIconMRaw from "../assets/game-elements/tanks/status-icon-m.svg?raw";
import tankDeadRaw from "../assets/game-elements/tanks/tank-dead.svg?raw";
import tankTeamARaw from "../assets/game-elements/tanks/tank-team-a.svg?raw";
import tankTeamBRaw from "../assets/game-elements/tanks/tank-team-b.svg?raw";
import unitDogARaw from "../assets/game-elements/tanks/unit-dog-a.svg?raw";
import unitDogBRaw from "../assets/game-elements/tanks/unit-dog-b.svg?raw";
import unitDroneARaw from "../assets/game-elements/tanks/unit-drone-a.svg?raw";
import unitDroneBRaw from "../assets/game-elements/tanks/unit-drone-b.svg?raw";
import unitTankARaw from "../assets/game-elements/tanks/unit-tank-a.svg?raw";
import unitTankBRaw from "../assets/game-elements/tanks/unit-tank-b.svg?raw";
import tileCoverRaw from "../assets/game-elements/tiles/tile-cover.svg?raw";
import tileEmptyRaw from "../assets/game-elements/tiles/tile-empty.svg?raw";
import tileObstacleRaw from "../assets/game-elements/tiles/tile-obstacle.svg?raw";
import tileSpawnARaw from "../assets/game-elements/tiles/tile-spawn-a.svg?raw";
import tileSpawnBRaw from "../assets/game-elements/tiles/tile-spawn-b.svg?raw";
import tileUnreachableRaw from "../assets/game-elements/tiles/tile-unreachable.svg?raw";
// V6 军事标注 pt (10) + 战术箭头 ar (3) RAW
import pt01CpRaw from "../assets/game-elements/annotations/pt-01-cp.svg?raw";
import pt02OpRaw from "../assets/game-elements/annotations/pt-02-op.svg?raw";
import pt03SpRaw from "../assets/game-elements/annotations/pt-03-sp.svg?raw";
import pt04BkrRaw from "../assets/game-elements/annotations/pt-04-bkr.svg?raw";
import pt05RuinRaw from "../assets/game-elements/annotations/pt-05-ruin.svg?raw";
import pt06TwrRaw from "../assets/game-elements/annotations/pt-06-twr.svg?raw";
import pt07AmmoRaw from "../assets/game-elements/annotations/pt-07-ammo.svg?raw";
import pt08BrdgRaw from "../assets/game-elements/annotations/pt-08-brdg.svg?raw";
import pt09FordRaw from "../assets/game-elements/annotations/pt-09-ford.svg?raw";
import pt10TreeRaw from "../assets/game-elements/annotations/pt-10-tree.svg?raw";
import ar01MainAttackRaw from "../assets/game-elements/annotations/ar-01-main-attack.svg?raw";
import ar02SupportAttackRaw from "../assets/game-elements/annotations/ar-02-support-attack.svg?raw";
import ar07WithdrawalRaw from "../assets/game-elements/annotations/ar-07-withdrawal.svg?raw";

const SVG_RAW = {
  "death-flash": deathFlashRaw,
  "fire-blast-a": fireBlastARaw,
  "fire-blast-b": fireBlastBRaw,
  "hit-burst": hitBurstRaw,
  "tombstone-glow": tombstoneGlowRaw,
  "callout-a-base": calloutABaseRaw,
  "callout-b-base": calloutBBaseRaw,
  "callout-midline": calloutMidlineRaw,
  "callout-spawn-a": calloutSpawnARaw,
  "callout-spawn-b": calloutSpawnBRaw,
  "callout-supply": calloutSupplyRaw,
  "coord-grid": coordGridRaw,
  "crosshair-120": crosshair120Raw,
  "crosshair-80": crosshair80Raw,
  "hp-bar-high": hpBarHighRaw,
  "hp-bar-low": hpBarLowRaw,
  "hp-bar-mid": hpBarMidRaw,
  "hud-corner-bl": hudCornerBlRaw,
  "hud-corner-br": hudCornerBrRaw,
  "hud-corner-tl": hudCornerTlRaw,
  "hud-corner-tr": hudCornerTrRaw,
  "mini-map-battle-easy": miniMapBattleEasyRaw,
  "spawn-marker-a": spawnMarkerARaw,
  "spawn-marker-b": spawnMarkerBRaw,
  "status-led-active": statusLedActiveRaw,
  "status-led-offline": statusLedOfflineRaw,
  "status-led-online": statusLedOnlineRaw,
  "status-led-warn": statusLedWarnRaw,
  "icon-algorithm": iconAlgorithmRaw,
  "icon-arrow-right": iconArrowRightRaw,
  "icon-book": iconBookRaw,
  "icon-chart": iconChartRaw,
  "icon-plus": iconPlusRaw,
  "icon-search": iconSearchRaw,
  "icon-shield": iconShieldRaw,
  "icon-star": iconStarRaw,
  "icon-sword": iconSwordRaw,
  "icon-terminal": iconTerminalRaw,
  "icon-think": iconThinkRaw,
  "icon-tip-card": iconTipCardRaw,
  "icon-tip": iconTipRaw,
  "icon-trash": iconTrashRaw,
  "operator-ai-aggro": operatorAiAggroRaw,
  "operator-tank": operatorTankRaw,
  "phase-climax": phaseClimaxRaw,
  "phase-contact": phaseContactRaw,
  "phase-endgame": phaseEndgameRaw,
  "phase-opening": phaseOpeningRaw,
  "phase-progress-bar": phaseProgressBarRaw,
  "status-icon-a": statusIconARaw,
  "status-icon-c": statusIconCRaw,
  "status-icon-d": statusIconDRaw,
  "status-icon-idle": statusIconIdleRaw,
  "status-icon-m": statusIconMRaw,
  "tank-dead": tankDeadRaw,
  "tank-team-a": tankTeamARaw,
  "tank-team-b": tankTeamBRaw,
  "unit-dog-a": unitDogARaw,
  "unit-dog-b": unitDogBRaw,
  "unit-drone-a": unitDroneARaw,
  "unit-drone-b": unitDroneBRaw,
  "unit-tank-a": unitTankARaw,
  "unit-tank-b": unitTankBRaw,
  "tile-cover": tileCoverRaw,
  "tile-empty": tileEmptyRaw,
  "tile-obstacle": tileObstacleRaw,
  "tile-spawn-a": tileSpawnARaw,
  "tile-spawn-b": tileSpawnBRaw,
  "tile-unreachable": tileUnreachableRaw,
  // V6 军事标注 pt + 战术箭头 ar RAW
  "pt-01-cp": pt01CpRaw,
  "pt-02-op": pt02OpRaw,
  "pt-03-sp": pt03SpRaw,
  "pt-04-bkr": pt04BkrRaw,
  "pt-05-ruin": pt05RuinRaw,
  "pt-06-twr": pt06TwrRaw,
  "pt-07-ammo": pt07AmmoRaw,
  "pt-08-brdg": pt08BrdgRaw,
  "pt-09-ford": pt09FordRaw,
  "pt-10-tree": pt10TreeRaw,
  "ar-01-main-attack": ar01MainAttackRaw,
  "ar-02-support-attack": ar02SupportAttackRaw,
  "ar-07-withdrawal": ar07WithdrawalRaw,
} as const satisfies Record<SvgKey, string>;

/**
 * 全局单例（屏 4 启动时用这一个 loader 即可）
 * 屏 1/3 集成时如果需要独立 loader，可 new SvgLoader() 创建新实例
 */
export const svgLoader = new SvgLoader();
